/**
 * Assembles the phase tag + per-phase view-arg bundles for RecordingControls.
 *
 * Extracted so `index.tsx` stays orchestration + render. Hook-call order inside
 * this module matches the previous inline block (permission → memos →
 * dismiss/finish wrappers → recovery memo) so behavior is unchanged.
 */
import type { TCoachTone } from "@lexicanon/shared"
import type { I18n } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import { useCallback, useMemo } from "react"
import type { SavedRecording } from "../../../hooks/useIndexedDB"
import { usePermissionState } from "../../../hooks/usePermissionState"
import type {
  IFacilitatorSuggestion,
  ILanguageSignal,
  ILanguageSuggestion,
  IProgressiveSummary,
  ISpeakerHypothesisSnapshot,
  IStreamingSegment,
} from "../../../hooks/useStreamingTranscript"
import { formatClock } from "../../../lib/format-date"
import { displayMeetingTitle } from "../../../lib/meeting-title"
import type { TProcessingStage } from "../../FinalizationProgress"
import type { TMicLevel } from "../../RecordingSurface/Visualizer"
import type { TMicAlert, TRecordingMode, TRecordingState } from "../interfaces"
import type {
  ILegacyAudioState,
  IProps as ILegacyProps,
  ILegacyRecordingHandlers,
} from "../LegacyRecordingPanel/interfaces"
import type { IFocusArgs, IPreRecordArgs, TRecordingPhase } from "../RecordingControlsView/interfaces"
import type { IProps as IRecoveryProps } from "../RecoveryBanner/interfaces"

/** First non-blank subject. A blank stays "" so Lite can show its own hint. */
function filledSubject(primary: string | undefined, fallback: string | undefined): string {
  if (primary?.trim()) return primary
  if (fallback?.trim()) return fallback
  return ""
}

interface IInitialDialogConfig {
  langs: string[] | null
  autoDetect: boolean
  mode: TRecordingMode | null
}

interface IHealthSnapshot {
  status: IFocusArgs["health"]
  label: string
  lastChecked: Date | null
  issues: string[]
}

export interface IUseRecordingViewArgsInput {
  product: string
  i18n: I18n
  meetingLabel: string
  meetingId: string
  meetingSubject?: string
  meetingStartTime?: string | null
  subject?: string
  onRenameMeeting?: () => void
  state: TRecordingState
  mode: TRecordingMode
  recovered: SavedRecording | null
  error: string
  setError: (err: string) => void
  finishing: boolean
  setFinishing: (v: boolean) => void
  submittingRecovery: boolean
  setSubmittingRecovery: (v: boolean) => void
  showFinishConfirm: boolean
  setShowFinishConfirm: (v: boolean) => void
  processingStage: TProcessingStage | null
  initialDialogConfig: IInitialDialogConfig | null
  languages: string[]
  setLanguages: (langs: string[]) => void
  setMode: (mode: TRecordingMode) => void
  monitoring: boolean
  devices: MediaDeviceInfo[]
  selectedDevice: string | null
  setSelectedDevice: (id: string | null) => void
  volume: number
  spectrum?: number[]
  boostedVolume: number
  boostEnabled: boolean
  startMonitoring: (deviceId: string) => Promise<void>
  handleBoostToggle: (enabled: boolean) => void
  elapsed: number
  isOnline: boolean
  wsReconnecting: boolean
  health: IHealthSnapshot
  liveSegments: IStreamingSegment[]
  interimText: string
  interimSpeaker: string
  transcriptStaleSec: number
  progressiveSummary: IProgressiveSummary | null
  progressiveSummaryCount: number
  progressiveSummaryAt: string | null
  handleStart: () => Promise<void>
  handlePause: () => void
  handleContinue: () => void
  handleFinish: () => Promise<void>
  handleResumeRecovery: () => void
  handleSubmitRecovery: () => Promise<void>
  handleDiscardRecovery: () => void
  coachEnabled: boolean
  coachTone: TCoachTone
  facilitatorSuggestion: IFacilitatorSuggestion | null
  facilitatorHistory: IFacilitatorSuggestion[]
  coachAsking: boolean
  handleCoachToggle: (enabled: boolean) => void
  handleCoachToneChange: (tone: TCoachTone) => void
  handleCoachAsk: () => void
  speakerHypotheses: Map<string, ISpeakerHypothesisSnapshot>
  hypothesisPending: Set<string>
  handleHypothesisAction: (label: string, name: string, action: "confirm" | "reject") => void
  idbWarning: boolean
  uploading: boolean
  uploadCount: number
  streamingAvailable: boolean
  streamingAdapterList: { name: string; label: string }[]
  selectedBackend: string | null
  defaultAdapterName: string
  setSelectedBackend: (backend: string | null) => void
  identifiedSpeakers: string[]
  micAlert: TMicAlert
  showTranscript: boolean
  setShowTranscript: React.Dispatch<React.SetStateAction<boolean>>
  testRecording: boolean
  testCountdown: number
  testAudioUrl: string | null
  handleTestMic: () => void
  setSceneInsightsOpen: (open: boolean) => void
  /** Lite experience — recording/paused/finalizing routes to "lite-focus". */
  isLite?: boolean
  languageSignal?: ILanguageSignal | null
  meetingLanguage?: string | null
  /** First code of the workspace `default_language` setting, if any. */
  workspaceDefaultLanguage?: string
  /** One-shot language prompt from the live transcript hook. */
  languageSuggestion?: ILanguageSuggestion | null
  /** Tell the server to switch the meeting to the suggested language. */
  onAcceptLanguageSwitch?: () => void
  /** Tell the server to keep the current language for the suggested candidate. */
  onKeepCurrentLanguage?: () => void
}

export interface IUseRecordingViewArgs {
  phase: TRecordingPhase
  useLexicanonFocus: boolean
  prerecordArgs: IPreRecordArgs | undefined
  focusArgs: IFocusArgs | undefined
  recoveryArgs: IRecoveryProps | undefined
  legacyArgs: ILegacyProps | undefined
}

export function useRecordingViewArgs(input: IUseRecordingViewArgsInput): IUseRecordingViewArgs {
  const {
    product,
    i18n,
    meetingLabel,
    meetingId,
    meetingSubject,
    meetingStartTime,
    subject,
    onRenameMeeting,
    state,
    mode,
    recovered,
    error,
    setError,
    finishing,
    setFinishing,
    submittingRecovery,
    setSubmittingRecovery,
    showFinishConfirm,
    setShowFinishConfirm,
    processingStage,
    initialDialogConfig,
    languages,
    setLanguages,
    setMode,
    monitoring,
    devices,
    selectedDevice,
    setSelectedDevice,
    volume,
    spectrum,
    boostedVolume,
    boostEnabled,
    startMonitoring,
    handleBoostToggle,
    elapsed,
    isOnline,
    wsReconnecting,
    health,
    liveSegments,
    interimText,
    interimSpeaker,
    transcriptStaleSec,
    progressiveSummary,
    progressiveSummaryCount,
    progressiveSummaryAt,
    handleStart,
    handlePause,
    handleContinue,
    handleFinish,
    handleResumeRecovery,
    handleSubmitRecovery,
    handleDiscardRecovery,
    coachEnabled,
    coachTone,
    facilitatorSuggestion,
    facilitatorHistory,
    coachAsking,
    handleCoachToggle,
    handleCoachToneChange,
    handleCoachAsk,
    speakerHypotheses,
    hypothesisPending,
    handleHypothesisAction,
    idbWarning,
    uploading,
    uploadCount,
    streamingAvailable,
    streamingAdapterList,
    selectedBackend,
    defaultAdapterName,
    setSelectedBackend,
    identifiedSpeakers,
    micAlert,
    showTranscript,
    setShowTranscript,
    testRecording,
    testCountdown,
    testAudioUrl,
    handleTestMic,
    setSceneInsightsOpen,
    isLite = false,
    languageSignal = null,
    meetingLanguage = null,
    workspaceDefaultLanguage,
    languageSuggestion = null,
    onAcceptLanguageSwitch,
    onKeepCurrentLanguage,
  } = input

  // biome-ignore lint/suspicious/noExplicitAny: PermissionName "microphone" is widely supported but not in lib.dom yet
  const micPermission = usePermissionState("microphone" as any) // eslint-disable-line @typescript-eslint/no-explicit-any

  const modeLabel = useMemo(
    () => (mode === "streaming" ? i18n._(msg`Live transcript`) : i18n._(msg`Batch summary`)),
    [mode, i18n],
  )

  const dialogLanguages = useMemo(() => {
    if (initialDialogConfig?.autoDetect) return [] as string[]
    return initialDialogConfig?.langs ?? languages
  }, [initialDialogConfig, languages])

  const useLexicanonFocus = product === "lexicanon"

  const phase: TRecordingPhase = useMemo(() => {
    if (recovered) return "recovery"
    if (useLexicanonFocus && state === "idle") return "prerecord"
    if (useLexicanonFocus && (state === "recording" || state === "paused" || state === "finalizing")) {
      return isLite ? "lite-focus" : "focus"
    }
    return "legacy"
  }, [recovered, state, useLexicanonFocus, isLite])

  const focusLevel: TMicLevel = useMemo(() => {
    if (state !== "recording") return "silent"
    if (volume >= 0.251) return "clipping"
    if (volume <= 0.025) return "silent"
    return "normal"
  }, [state, volume])

  // `error` is the empty string when clear; the Lexicanon surfaces want
  // null-or-message so the Alert renders only when there's something to show.
  const errorMessage = error.length > 0 ? error : null
  const handleDismissError = useCallback((): void => {
    setError("")
  }, [setError])

  // Finish + Submit-recovery wrappers that drive the button loading/disabled
  // state for the async duration. The underlying handlers carry their own
  // re-entry guards (see §1.5) — these just surface the in-flight UX.
  const handleConfirmFinish = useCallback((): void => {
    setFinishing(true)
    handleFinish()
      .catch(() => undefined)
      .finally(() => setFinishing(false))
  }, [handleFinish, setFinishing])

  const handleSubmitRecoveryWithFlag = useCallback((): void => {
    setSubmittingRecovery(true)
    handleSubmitRecovery()
      .catch(() => undefined)
      .finally(() => setSubmittingRecovery(false))
  }, [handleSubmitRecovery, setSubmittingRecovery])

  const focusAudio: IPreRecordArgs["audio"] = {
    monitoring,
    devices,
    selectedDevice,
    setSelectedDevice: (id: string): void => {
      setSelectedDevice(id)
    },
    volume,
    spectrum,
    boostEnabled,
    startMonitoring,
    handleBoostToggle,
  }

  const requestMicrophone = useCallback((): void => {
    startMonitoring(selectedDevice ?? "default").catch(() => undefined)
  }, [selectedDevice, startMonitoring])

  const prerecordArgs: IPreRecordArgs | undefined =
    phase === "prerecord"
      ? {
          subject: displayMeetingTitle(filledSubject(meetingSubject, subject), null, i18n, { lite: isLite }),
          startTime: meetingStartTime ?? null,
          permission: micPermission,
          languages: dialogLanguages,
          autoDetect: initialDialogConfig?.autoDetect ?? false,
          mode: isLite ? "lite" : "full",
          transcription: mode === "batch" ? "batch" : "live",
          modeLabel,
          audio: focusAudio,
          onRecheckPermission: requestMicrophone,
          onAllowMicrophone: requestMicrophone,
          onStart: () => {
            handleStart().catch(() => undefined)
          },
          onRename: onRenameMeeting,
          error: errorMessage,
          onDismissError: handleDismissError,
        }
      : undefined

  const languageCode = meetingLanguage ?? languageSignal?.current ?? dialogLanguages[0] ?? languages[0]
  const languageIsWorkspaceDefault = Boolean(
    languageCode && workspaceDefaultLanguage && languageCode === workspaceDefaultLanguage,
  )

  const isFocusPhase = phase === "focus" || phase === "lite-focus"

  const focusArgs: IFocusArgs | undefined = isFocusPhase
    ? {
        paused: state === "paused",
        finalizing: state === "finalizing",
        offlineRecordingOnly: !isOnline,
        error: errorMessage,
        onDismissError: handleDismissError,
        wsReconnecting,
        health: health.status,
        healthIssues: health.issues,
        finishing,
        processingStage,
        subject: isLite
          ? filledSubject(meetingSubject, subject)
          : displayMeetingTitle(filledSubject(meetingSubject, subject), null, i18n),
        startTime: meetingStartTime ?? null,
        elapsed,
        level: focusLevel,
        audio: focusAudio,
        segments: liveSegments,
        interimText,
        interimSpeaker,
        transcriptStaleSec,
        progressiveSummary,
        progressiveSummaryCount,
        progressiveSummaryAt,
        finishConfirmOpen: showFinishConfirm,
        meetingLabel,
        meetingId,
        onPause: handlePause,
        onResume: handleContinue,
        onRequestFinish: () => setShowFinishConfirm(true),
        onConfirmFinish: handleConfirmFinish,
        onCancelFinish: () => setShowFinishConfirm(false),
        onRename: onRenameMeeting,
        coachEnabled,
        coachTone,
        coachSuggestion: facilitatorSuggestion,
        coachHistory: facilitatorHistory,
        coachAsking,
        onCoachToggle: handleCoachToggle,
        onCoachToneChange: handleCoachToneChange,
        onCoachAsk: handleCoachAsk,
        speakerHypotheses: [...speakerHypotheses.values()],
        hypothesisPending,
        onHypothesisAction: handleHypothesisAction,
        languageCode,
        languageSignal,
        languageIsWorkspaceDefault,
        languageSuggestion,
        onAcceptLanguageSwitch,
        onKeepCurrentLanguage,
      }
    : undefined

  const recoveryArgs = useMemo(() => {
    if (!recovered) return undefined
    const dur = Math.floor((recovered.savedAt - recovered.startTime) / 1000)
    const sizeMB = (recovered.chunks.reduce((a, c) => a + c.size, 0) / 1024 / 1024).toFixed(1)
    return {
      durationLabel: formatClock(dur),
      sizeMB,
      monitoring,
      onResume: handleResumeRecovery,
      onSubmit: handleSubmitRecoveryWithFlag,
      onDiscard: handleDiscardRecovery,
      submitting: submittingRecovery,
    }
  }, [
    recovered,
    monitoring,
    handleResumeRecovery,
    handleSubmitRecoveryWithFlag,
    handleDiscardRecovery,
    submittingRecovery,
  ])

  const legacyAudio: ILegacyAudioState = {
    monitoring,
    devices,
    selectedDevice,
    setSelectedDevice,
    volume,
    boostedVolume,
    boostEnabled,
    startMonitoring,
    onBoostToggle: handleBoostToggle,
  }

  const legacyHandlers: ILegacyRecordingHandlers = {
    onStart: () => {
      handleStart().catch(() => undefined)
    },
    onPause: handlePause,
    onResume: handleContinue,
    onRequestFinish: () => setShowFinishConfirm(true),
    onConfirmFinish: () => {
      handleFinish().catch(() => undefined)
    },
    onCancelFinish: () => setShowFinishConfirm(false),
    onStartTestMic: handleTestMic,
    onModeChange: setMode,
    onLanguagesChange: setLanguages,
    onBackendChange: setSelectedBackend,
    onToggleTranscriptVisible: () => setShowTranscript((v) => !v),
    onOpenSceneInsights: () => setSceneInsightsOpen(true),
    onCloseSceneInsights: () => setSceneInsightsOpen(false),
  }

  const legacyArgs: ILegacyProps | undefined =
    phase === "legacy"
      ? {
          state,
          mode,
          meetingLabel,
          product: product as ILegacyProps["product"],
          meetingId,
          isOnline,
          idbWarning,
          uploading,
          uploadCount,
          health: {
            status: health.status,
            label: health.label,
            lastChecked: health.lastChecked ?? null,
            issues: health.issues,
          },
          wsReconnecting,
          streamingAvailable,
          streamingAdapterList,
          selectedBackend,
          defaultAdapterName,
          languages,
          identifiedSpeakers,
          processingStage,
          micAlert,
          elapsed,
          transcriptStaleSec,
          segments: liveSegments,
          interimText,
          interimSpeaker,
          speakerHypotheses,
          hypothesisPending,
          onHypothesisAction: handleHypothesisAction,
          showTranscript,
          progressiveSummary,
          testRecording,
          testCountdown,
          testAudioUrl,
          testAudioClassName: "",
          finishConfirmOpen: showFinishConfirm,
          error,
          audio: legacyAudio,
          handlers: legacyHandlers,
        }
      : undefined

  return { phase, useLexicanonFocus, prerecordArgs, focusArgs, recoveryArgs, legacyArgs }
}
